import {
  ArrowRight,
  Check,
  Database,
  LayoutDashboard,
  LineChart,
  Plus,
  RefreshCw,
  Share2,
  Sparkles,
  Star,
  TriangleAlert,
} from 'lucide-react';
import { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { cn } from '../../../lib/cn';
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  PageHeader,
  SectionHeader,
  Skeleton,
  errorMessage,
} from '../../../ui';
import { useAssistant } from '../assistant/AssistantContext';
import { ChartRenderer } from '../components/ChartRenderer';
import { useApiSources, useCharts, useDashboards, useSourceOutages } from '../hooks';
import { tileRefs } from '../dashboard/layout';
import { familyForType, iconForType } from '../lib/chartFamilies';
import { humanizeType, plural, relativeTime, timestamp } from '../lib/format';
import type { SourceOutage } from '../lib/api';
import type { Chart, Dashboard } from '../lib/types';
import { useFavorites } from '../shell/prefs';

/* ============================================================================
   Home — a decision surface, not a wall of tiles.

   It answers, in this order: is anything wrong, what changed, what do I care
   about, and what should I do next. The previous version led with four count
   tiles — one of which read "AI Insights: Beta", a metric with no meaning — and
   then three quick-action cards duplicating the sidebar.
   ========================================================================== */

interface Attention {
  id: string;
  label: string;
  detail: string;
  to: string;
  cta: string;
}

/**
 * Things that are actually wrong, from data we already hold.
 *
 * Deliberately conservative: each of these is a fact about the workspace, not a
 * guess. Probing every chart's upstream source to find broken ones would mean a
 * request per chart on the landing page, which is a worse trade than surfacing
 * the problems we can see for free.
 */
function attentionItems(
  charts: Chart[],
  dashboards: Dashboard[],
  sourceCount: number,
  outages: SourceOutage[],
): Attention[] {
  const items: Attention[] = [];

  const unbound = charts.filter((c) => !c.data_sources?.length);
  if (unbound.length) {
    items.push({
      id: 'unbound-charts',
      label: `${plural(unbound.length, 'chart')} not bound to any data`,
      detail: 'They will render empty until a data source and axes are set.',
      to: `/charts?filter=unbound`,
      cta: 'Review',
    });
  }

  const emptyBoards = dashboards.filter((d) => tileRefs(d.layout).length === 0);
  if (emptyBoards.length) {
    items.push({
      id: 'empty-dashboards',
      label: `${plural(emptyBoards.length, 'dashboard')} with no tiles`,
      detail: 'Add a chart, or delete the board if it was created by accident.',
      to: '/dashboards',
      cta: 'Open',
    });
  }

  /*
   * An empty source list has two very different causes, and for a long time
   * this said the wrong one.
   *
   * "No data sources are connected · Connect" was shown to people with sixteen
   * sources, whose real problem was that the application owning the data would
   * not let analytics read on their behalf. The advice was not merely useless,
   * it was misdirection: adding a seventeenth source cannot fix a permission.
   *
   * AG-1274 made the backend say which it is (424, naming the target), so this
   * can now distinguish "you have none" from "we were refused" from "we could
   * not reach them" — and only the first is about connecting anything.
   */
  if (charts.length > 0 && sourceCount === 0) {
    const refused = outages.filter((o) => o.reason === 'delegation');
    const unreachable = outages.filter((o) => o.reason === 'unreachable');

    if (refused.length) {
      const who = refused.map((o) => o.target).filter(Boolean).join(' and ') ||
        'The application that owns your data';
      // Which of three stories this is, decided by the backend where the facts
      // are. Do not guess: telling somebody to sign in differently when that
      // cannot help is worse than saying less.
      //
      // ⭐ The third one was missing, and it is the common one. A person signed
      // in on analytics' own form is signed into Analytics ALONE — the shared
      // suite cookie is set on the connection Identity Studio answers, and for
      // that form the connection is the backend's. So delegation is refused,
      // and it is refused identically when the browser's suite session belongs
      // to a *different account*. Both were being reported as a missing
      // cross-application grant: an administrator's problem, stated to somebody
      // who could have fixed it themselves in a press. `AccountMismatchGate`
      // offers that press; this says the same thing, because a banner and a
      // dialog disagreeing about one refusal is its own bug.
      const mismatch = refused.some((o) => o.remedy === 'sign_in_to_suite');
      const noAccess = refused.some((o) => o.remedy === 'wrong_account');
      const withDelegation = refused.some((o) => o.delegated === true);
      const detail = mismatch
        ? `Analytics and ${who} are not signed in as the same account in this ` +
          'browser, so your sources are there but unreadable. Sign in across the ' +
          'suite with the account that owns them — adding another source will not help.'
        : noAccess
          ? `The account you are signed in as may not use ${who}, so its data ` +
            'cannot be read for you. Sign in with the account that owns it, or ask ' +
            'for access to be added.'
          : withDelegation
            ? `${who} refused a request analytics was authorised to make for you. ` +
              'Your sign-in is valid and your sources are still there — the refusal ' +
              'came from their side, so this may clear on its own.'
            : `Analytics is not permitted to read on your behalf at ${who}, so your ` +
              'sources are there but unreadable. Your sign-in is valid and adding ' +
              'another source will not help — this is a cross-application permission ' +
              'that has to be granted in Identity Studio.';
      items.push({
        id: 'sources-refused',
        label: 'Your data sources could not be read',
        detail,
        to: '/data',
        cta: 'Retry',
      });
    } else if (unreachable.length) {
      items.push({
        id: 'sources-unreachable',
        label: 'Your data sources could not be reached',
        detail:
          'Nothing answered for them just now, so this may clear on its own. ' +
          'They are not missing — this is not something to fix by adding one.',
        to: '/data',
        cta: 'Retry',
      });
    } else {
      items.push({
        id: 'no-sources',
        label: 'No data sources are connected',
        detail: 'Every chart will fail to load until at least one source is reachable.',
        to: '/data',
        cta: 'Connect',
      });
    }
  }

  return items;
}

export default function HomePage() {
  const navigate = useNavigate();
  const { favorites } = useFavorites();

  const chartsQuery = useCharts();
  const dashboardsQuery = useDashboards();
  const { data: charts, isLoading: chartsLoading, refetch: refetchCharts } = chartsQuery;
  const { data: dashboards, isLoading: dashboardsLoading } = dashboardsQuery;
  const { data: sources, isLoading: sourcesLoading } = useApiSources();
  const { data: outages } = useSourceOutages();

  // Memoised so the `?? []` fallback does not hand every memo below a brand-new
  // array on each render.
  const chartList = useMemo(() => charts ?? [], [charts]);
  const dashboardList = useMemo(() => dashboards ?? [], [dashboards]);
  const sourceList = useMemo(() => sources ?? [], [sources]);
  const outageList = useMemo(() => outages ?? [], [outages]);
  const loading = chartsLoading || dashboardsLoading || sourcesLoading;

  const recent = useMemo(
    () =>
      [...chartList]
        .sort((a, b) => timestamp(b.updated_at || b.created_at) - timestamp(a.updated_at || a.created_at))
        .slice(0, 4),
    [chartList],
  );

  const recentBoards = useMemo(
    () =>
      [...dashboardList]
        .sort((a, b) => timestamp(b.updated_at || b.created_at) - timestamp(a.updated_at || a.created_at))
        .slice(0, 4),
    [dashboardList],
  );

  const starred = useMemo(
    () => [
      ...chartList.filter((c) => favorites.has(c.id)).map((c) => ({ kind: 'chart' as const, item: c })),
      ...dashboardList.filter((d) => favorites.has(d.id)).map((d) => ({ kind: 'dashboard' as const, item: d })),
    ],
    [chartList, dashboardList, favorites],
  );

  const attention = attentionItems(chartList, dashboardList, sourceList.length, outageList);
  // A failed load is NOT an empty workspace. Telling a user with 200 charts that
  // they have none, and offering them a setup checklist, is the worst possible
  // reading of a 502.
  /**
   * A load has FAILED once it has either errored outright, or failed at least
   * once and then stalled (the query layer pauses retries when it thinks the
   * browser is offline). Without the second case a stalled load shows skeletons
   * forever with nothing to act on.
   */
  const stalled = (q: {
    isError: boolean;
    fetchStatus: 'fetching' | 'paused' | 'idle';
    failureCount: number;
  }) => q.isError || (q.fetchStatus === 'paused' && q.failureCount > 0);
  const failure = chartsQuery.error ?? chartsQuery.failureReason ?? dashboardsQuery.error ?? dashboardsQuery.failureReason;
  const loadFailed = stalled(chartsQuery) || stalled(dashboardsQuery);
  const isNewWorkspace = !loading && !loadFailed && chartList.length === 0 && dashboardList.length === 0;

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';

  return (
    <>
      {/*
        No "Ask the assistant" action here: the shell's top bar carries a global
        Assistant toggle (⌘J) on every screen, and the two buttons sat side by
        side doing the same thing.
      */}
      <PageHeader
        title={greeting}
        description="Your analytics workspace at a glance."
        actions={
          <Button variant="primary" icon={<Plus />} onClick={() => navigate('/charts/new')}>
            New chart
          </Button>
        }
      />

      <div className="scroll-region flex-1 px-gutter py-gutter">
        <div className="mx-auto flex max-w-6xl flex-col gap-section">
          {loadFailed && (
            <Alert
              tone="danger"
              icon={TriangleAlert}
              title="Your workspace could not be loaded"
              actions={
                <Button size="sm" variant="secondary" icon={<RefreshCw />} onClick={() => void refetchCharts()}>
                  Try again
                </Button>
              }
            >
              {errorMessage(failure)} Nothing has been lost — this is a problem reaching the server, not
              with your charts.
            </Alert>
          )}

          {isNewWorkspace ? (
            <SetupChecklist sourceCount={sourceList.length} />
          ) : loadFailed ? null : (
            <>
              {/* Is something wrong? */}
              {attention.length > 0 && (
                <section aria-labelledby="attention-heading">
                  <SectionHeader
                    title={<span id="attention-heading">Needs attention</span>}
                    description="Found in your workspace — nothing here is guessed."
                  />
                  <div className="flex flex-col gap-2">
                    {attention.map((a) => (
                      <Alert
                        key={a.id}
                        tone="warning"
                        icon={TriangleAlert}
                        title={a.label}
                        actions={
                          <Button size="sm" variant="secondary" asChild>
                            <Link to={a.to}>
                              {a.cta}
                              <ArrowRight />
                            </Link>
                          </Button>
                        }
                      >
                        {a.detail}
                      </Alert>
                    ))}
                  </div>
                </section>
              )}

              {/* What is happening? */}
              <section aria-labelledby="overview-heading">
                <SectionHeader title={<span id="overview-heading">Workspace</span>} />
                <div className="grid gap-3 sm:grid-cols-3">
                  <CountCard
                    to="/charts"
                    icon={LineChart}
                    label="Charts"
                    value={chartList.length}
                    loading={chartsLoading}
                    hint={recent[0] ? `Last edited ${relativeTime(recent[0].updated_at)}` : 'None yet'}
                  />
                  <CountCard
                    to="/dashboards"
                    icon={LayoutDashboard}
                    label="Dashboards"
                    value={dashboardList.length}
                    loading={dashboardsLoading}
                    hint={
                      recentBoards[0]
                        ? `Last edited ${relativeTime(recentBoards[0].updated_at)}`
                        : 'None yet'
                    }
                  />
                  <CountCard
                    to="/data"
                    icon={Database}
                    label="Data sources"
                    value={sourceList.length}
                    loading={sourcesLoading}
                    hint={
                      sourceList.length
                        ? `${sourceList.filter((s) => s.kind === 'webhook').length} live streams`
                        : 'Connect one to start'
                    }
                  />
                </div>
              </section>

              {/* What do I care about? */}
              {starred.length > 0 && (
                <section aria-labelledby="starred-heading">
                  <SectionHeader
                    title={
                      <span id="starred-heading" className="flex items-center gap-1.5">
                        <Star className="size-3.5 fill-warning text-warning" aria-hidden />
                        Starred
                      </span>
                    }
                  />
                  <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                    {starred.slice(0, 6).map(({ kind, item }) => {
                      const Icon = kind === 'chart' ? iconForType((item as Chart).chart_type) : LayoutDashboard;
                      return (
                        <li key={`${kind}:${item.id}`}>
                          <Card interactive className="flex items-center gap-2.5 p-2.5">
                            <Link
                              to={kind === 'chart' ? `/charts/${item.id}` : `/dashboards/${item.id}`}
                              className="flex min-w-0 flex-1 items-center gap-2.5 rounded-md focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
                            >
                              <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-surface-hover text-fg-muted">
                                <Icon className="size-4" />
                              </span>
                              <span className="min-w-0 flex-1">
                                <span className="block truncate text-[0.8125rem] font-medium text-fg">
                                  {item.name}
                                </span>
                                <span className="block truncate text-xs text-fg-muted">
                                  {kind === 'chart' ? humanizeType((item as Chart).chart_type) : 'Dashboard'}
                                </span>
                              </span>
                            </Link>
                          </Card>
                        </li>
                      );
                    })}
                  </ul>
                </section>
              )}

              {/* What changed? */}
              <section aria-labelledby="recent-heading">
                <SectionHeader
                  title={<span id="recent-heading">Recently edited</span>}
                  actions={
                    <Button variant="link" size="sm" asChild>
                      <Link to="/charts">
                        All charts
                        <ArrowRight />
                      </Link>
                    </Button>
                  }
                />
                {chartsLoading ? (
                  <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                    {Array.from({ length: 4 }, (_, i) => (
                      <Skeleton key={i} className="h-40" />
                    ))}
                  </div>
                ) : recent.length === 0 ? (
                  <Card tone="subtle">
                    <EmptyState
                      size="sm"
                      icon={LineChart}
                      title="No charts yet"
                      description="Build one from a data source, or describe what you want and let the assistant do it."
                      action={
                        <Button size="sm" variant="primary" icon={<Plus />} onClick={() => navigate('/charts/new')}>
                          New chart
                        </Button>
                      }
                    />
                  </Card>
                ) : (
                  <ul className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                    {recent.map((c) => (
                      <li key={c.id}>
                        <RecentChartCard chart={c} />
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              {recentBoards.length > 0 && (
                <section aria-labelledby="boards-heading">
                  <SectionHeader
                    title={<span id="boards-heading">Dashboards</span>}
                    actions={
                      <Button variant="link" size="sm" asChild>
                        <Link to="/dashboards">
                          All dashboards
                          <ArrowRight />
                        </Link>
                      </Button>
                    }
                  />
                  <ul className="grid gap-2 sm:grid-cols-2">
                    {recentBoards.map((d) => {
                      const tiles = tileRefs(d.layout).length;
                      return (
                        <li key={d.id}>
                          <Card interactive className="p-0">
                            <Link
                              to={`/dashboards/${d.id}`}
                              className="flex items-center gap-3 rounded-lg p-3 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
                            >
                              <span className="flex size-9 shrink-0 items-center justify-center rounded-md bg-surface-hover text-fg-muted">
                                <LayoutDashboard className="size-4" />
                              </span>
                              <span className="min-w-0 flex-1">
                                <span className="flex items-center gap-2">
                                  <span className="truncate text-[0.8125rem] font-medium text-fg">{d.name}</span>
                                  {d.is_draft && (
                                    <Badge tone="warning" size="sm">
                                      Draft
                                    </Badge>
                                  )}
                                </span>
                                <span className="block truncate text-xs text-fg-muted">
                                  {plural(tiles, 'tile')} · updated {relativeTime(d.updated_at || d.created_at)}
                                </span>
                              </span>
                              <ArrowRight className="size-4 shrink-0 text-fg-faint" aria-hidden />
                            </Link>
                          </Card>
                        </li>
                      );
                    })}
                  </ul>
                </section>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}

function CountCard({
  to,
  icon: Icon,
  label,
  value,
  hint,
  loading,
}: {
  to: string;
  icon: typeof LineChart;
  label: string;
  value: number;
  hint: string;
  loading?: boolean;
}) {
  return (
    <Card interactive className="p-0">
      <Link
        to={to}
        className="flex items-center gap-3 rounded-lg p-4 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
      >
        <span className="flex size-9 shrink-0 items-center justify-center rounded-md bg-surface-hover text-fg-muted">
          <Icon className="size-4" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block font-display text-xl font-semibold text-fg tabular">
            {loading ? <Skeleton className="h-6 w-10" /> : value}
          </span>
          <span className="block text-xs font-medium text-fg-secondary">{label}</span>
          <span className="block truncate text-xs text-fg-faint">{hint}</span>
        </span>
      </Link>
    </Card>
  );
}

function RecentChartCard({ chart }: { chart: Chart }) {
  const family = familyForType(chart.chart_type);
  return (
    <Card interactive className="flex h-full flex-col overflow-hidden p-0">
      <Link
        to={`/charts/${chart.id}`}
        className="flex min-h-0 flex-1 flex-col rounded-lg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
      >
        <span className="flex items-start gap-2 border-b border-border-subtle p-2.5">
          <span
            className="mt-px size-2 shrink-0 rounded-full"
            style={{ background: family.fg }}
            aria-hidden
          />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-xs font-medium text-fg">{chart.name}</span>
            <span className="block truncate text-[0.6875rem] text-fg-faint">
              {humanizeType(chart.chart_type)} · {relativeTime(chart.updated_at || chart.created_at)}
            </span>
          </span>
        </span>
        <span className={cn('pointer-events-none block h-28 w-full p-1.5')}>
          <ChartRenderer chartId={chart.id} height={104} compact />
        </span>
      </Link>
    </Card>
  );
}

/**
 * First-run guidance.
 *
 * The workspace has a genuine order of operations — a source, then a chart, then
 * a board, then a link — and each step is checkable from data we already have.
 */
function SetupChecklist({ sourceCount }: { sourceCount: number }) {
  const navigate = useNavigate();
  const assistant = useAssistant();

  const steps = [
    {
      id: 'source',
      title: 'Connect data',
      body: 'An HTTP API, a spreadsheet, or a webhook other systems push rows into.',
      done: sourceCount > 0,
      action: (
        <Button size="sm" variant="secondary" icon={<Database />} onClick={() => navigate('/data')}>
          Connect a source
        </Button>
      ),
    },
    {
      id: 'chart',
      title: 'Build a chart',
      body: 'Drag fields onto axes, pick a type, and see real data as you go.',
      done: false,
      action: (
        <Button size="sm" variant="primary" icon={<Plus />} onClick={() => navigate('/charts/new')}>
          New chart
        </Button>
      ),
    },
    {
      id: 'dashboard',
      title: 'Assemble a dashboard',
      body: 'Or describe the board you need and let the assistant plan, bind and design it.',
      done: false,
      action: (
        <Button size="sm" variant="secondary" icon={<Sparkles />} onClick={() => assistant.setOpen(true)}>
          Ask the assistant
        </Button>
      ),
    },
    {
      id: 'share',
      title: 'Share it',
      body: 'Publish a read-only link — live or frozen — that needs no login.',
      done: false,
      action: null,
    },
  ];

  return (
    <section aria-labelledby="setup-heading">
      <div className="rounded-xl border border-border bg-surface p-5">
        <div className="brand-rule mb-4 h-0.5 w-12 rounded-full" aria-hidden />
        <h2 id="setup-heading" className="font-display text-base font-semibold text-fg">
          Set up your workspace
        </h2>
        <p className="mt-1 max-w-prose text-[0.8125rem] leading-relaxed text-fg-muted">
          Four steps, in order. You can also skip straight to the assistant and describe the dashboard you
          need — it will find the data itself.
        </p>
        <ol className="mt-5 flex flex-col gap-3">
          {steps.map((step, i) => (
            <li key={step.id} className="flex gap-3">
              <span
                className={cn(
                  'flex size-6 shrink-0 items-center justify-center rounded-full border text-xs font-semibold',
                  step.done
                    ? 'border-success bg-success text-white'
                    : 'border-border bg-surface-subtle text-fg-muted',
                )}
                aria-hidden
              >
                {step.done ? <Check className="size-3.5" strokeWidth={3} /> : i + 1}
              </span>
              <div className="min-w-0 flex-1 pb-1">
                <p className="text-[0.8125rem] font-medium text-fg">
                  {step.title}
                  {step.done && (
                    <Badge tone="success" size="sm" className="ml-2">
                      Done
                    </Badge>
                  )}
                </p>
                <p className="mt-0.5 text-xs leading-relaxed text-fg-muted">{step.body}</p>
                {step.action && !step.done && <div className="mt-2">{step.action}</div>}
              </div>
            </li>
          ))}
        </ol>
        <div className="mt-5 flex items-center gap-2 border-t border-border-subtle pt-4 text-xs text-fg-muted">
          <Share2 className="size-3.5" aria-hidden />
          Everything you build stays in your workspace until you publish a link.
        </div>
      </div>
    </section>
  );
}
